import React, { useState, useMemo } from "react";
import { Input } from "@/race-tracker/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Button } from "@/race-tracker/components/ui/button";
import { Search, Loader2, Zap } from "lucide-react";
import { differenceInHours } from "date-fns";

const ANALYSTS = ["Dan", "Evan", "Ian", "James"];

function freshnessLevel(sweepTimestamp) {
  if (!sweepTimestamp) return "never";
  const hours = differenceInHours(new Date(), new Date(sweepTimestamp));
  if (hours <= 24) return "green";
  if (hours <= 168) return "amber";
  return "red";
}

const DOT_COLORS = {
  green: "bg-emerald-500",
  amber: "bg-amber-400",
  red: "bg-red-500",
  never: "bg-red-500",
};

export default function RacePicker({
  races, adsMap, opponentsByRace, sweepMap, sweepingRaces,
  selectedRaceId, onSelectRace, onBulkSweep, bulkProgress
}) {
  const [search, setSearch] = useState("");
  const [daFilter, setDaFilter] = useState("all");
  const [sweepFilter, setSweepFilter] = useState("all");

  const filtered = useMemo(() => {
    return races.filter(r => {
      const ad = adsMap[r.advertiser_id];
      if (daFilter !== "all" && ad?.data_analyst !== daFilter) return false;
      const label = `${r.state} ${r.chamber} ${r.district || ""}`.toLowerCase();
      const opps = (opponentsByRace[r.id] || []).map(o => o.opponent_name.toLowerCase()).join(" ");
      if (search && !label.includes(search.toLowerCase()) && !opps.includes(search.toLowerCase()) && !r.state?.toLowerCase().includes(search.toLowerCase())) return false;
      if (sweepFilter !== "all") {
        const level = freshnessLevel(sweepMap[r.id]?.sweep_timestamp);
        if (sweepFilter === "needs" && level === "green") return false;
        if (sweepFilter === "fresh" && level !== "green") return false;
      }
      return true;
    });
  }, [races, adsMap, opponentsByRace, sweepMap, search, daFilter, sweepFilter]);

  const bulkRaces = daFilter !== "all"
    ? filtered
    : filtered;

  return (
    <div className="flex flex-col h-full">
      {/* Bulk sweep button */}
      <div className="p-3 border-b border-border">
        {bulkProgress ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-md px-3 py-2">
            <Loader2 className="w-3 h-3 animate-spin shrink-0" />
            Sweeping {bulkProgress.current} of {bulkProgress.total} races…
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs gap-1.5"
            onClick={() => onBulkSweep(bulkRaces)}
            disabled={bulkRaces.length === 0}
          >
            <Zap className="w-3 h-3" />
            Sweep All{daFilter !== "all" ? ` (${daFilter}'s)` : ""} Races
            <span className="ml-auto text-muted-foreground">({bulkRaces.length})</span>
          </Button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="p-3 space-y-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            className="pl-7 h-7 text-xs"
            placeholder="Search races, opponents…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5">
          <Select value={daFilter} onValueChange={setDaFilter}>
            <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Analyst" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Analysts</SelectItem>
              {ANALYSTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sweepFilter} onValueChange={setSweepFilter}>
            <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Sweep" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="needs">Needs Sweep</SelectItem>
              <SelectItem value="fresh">Recently Swept</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Race List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">No races match</p>
        ) : (
          filtered.map(r => {
            const ad = adsMap[r.advertiser_id];
            const opps = opponentsByRace[r.id] || [];
            const sweep = sweepMap[r.id];
            const level = freshnessLevel(sweep?.sweep_timestamp);
            const isSelected = r.id === selectedRaceId;
            const isSweeping = sweepingRaces.has(r.id);

            return (
              <button
                key={r.id}
                onClick={() => onSelectRace(r.id)}
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors flex items-start gap-2 ${
                  isSelected ? "bg-primary/8 border-l-2 border-l-primary" : "hover:bg-accent/50"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold font-mono">
                      {r.state} {r.chamber.replace("U.S. ", "").replace("State ", "")} {r.district || ""}
                    </span>
                    {ad?.data_analyst && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-700 font-medium shrink-0">
                        {ad.data_analyst[0]}
                      </span>
                    )}
                  </div>
                  {opps.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                      {opps.map(o => o.opponent_name).join(", ")}
                    </p>
                  )}
                </div>
                <div className="shrink-0 mt-1">
                  {isSweeping ? (
                    <Loader2 className="w-2.5 h-2.5 animate-spin text-primary" />
                  ) : (
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT_COLORS[level]}`} />
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}